import React from "react";

import { useMutation } from "@apollo/client";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import {
  Chip,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import useDialogState from "@/hooks/useDialogState";
import useFragment from "@/hooks/useFragment";
import useGlobalDialog from "@/hooks/useGlobalDialog";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import usePopupState from "@/hooks/usePopupState";
import { format, formatISODateTime, parseDateTime } from "@/utils/datetime";
import { displayError } from "@/utils/errors";

import RemarkForm, { RemarkValues } from "./RemarkForm";

interface Props {
  eventId: string;
  clientId: string;
  isFirst?: boolean;
  isLast?: boolean;
}

const timelineEntryFragment = graphql(`
  fragment TimelineEntry on Event {
    id
    type
    message
    createdAt
  }
`);

const updateEventRemarkMutation = graphql(`
  mutation UpdateEventRemark($input: UpdateEventRemarkInput!, $clientId: ID!) {
    updateEventRemark(input: $input) {
      event {
        ...TimelineEntry
      }
      query {
        # Refetch client last event and events in case remark created at is
        # changed, in order to update the sorting of timeline in the UI.
        client(id: $clientId) {
          id
          lastEvent {
            ...TimelineEntry
          }
          events(order: DESC) {
            nodes {
              ...TimelineEntry
            }
          }
        }
      }
    }
  }
`);

const deleteEventRemarkMutation = graphql(`
  mutation DeleteEventRemark($id: ID!, $clientId: ID!) {
    deleteEventRemark(input: { id: $id }) {
      event {
        ...TimelineEntry
      }
      query {
        # Refetch client last event and events.
        client(id: $clientId) {
          id
          lastEvent {
            ...TimelineEntry
          }
          events(order: DESC) {
            nodes {
              ...TimelineEntry
            }
          }
        }
      }
    }
  }
`);

const useStyles = makeStyles()({
  upConnector: {
    height: "11.5px",
    flexGrow: 0,
  },
  dot: {
    margin: "0",
  },
  content: {
    paddingRight: 0,
  },
  remarkHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  remarkChip: {
    borderRadius: 4,
  },
  message: {
    fontSize: 14,
    fontWeight: "600",
    letterSpacing: "-0.5px",
    color: "#525252",
    whiteSpace: "pre-wrap",
  },
  createdAt: {
    fontSize: 12,
    fontWeight: "600",
    letterSpacing: "-0.5px",
    color: "#7D8797",
  },
});

const TimelineEntry: React.FC<Props> = ({
  eventId,
  clientId,
  isFirst,
  isLast,
}) => {
  const { classes } = useStyles();
  const [event] = useFragment(timelineEntryFragment, `Event:${eventId}`);
  const snackbar = useGlobalSnackbar();
  const dialog = useGlobalDialog();
  const optionMenu = usePopupState();
  const editRemarkForm = useDialogState();

  const [updateEventRemark] = useMutation(updateEventRemarkMutation);
  const updateRemark = async (values: RemarkValues) => {
    try {
      await updateEventRemark({
        variables: {
          input: {
            comment: values.comment,
            createdAt: formatISODateTime(values.createdAt),
            id: eventId,
          },
          clientId,
        },
      });
      editRemarkForm.hide();
      snackbar.open("Remark successfully updated.");
    } catch (e) {
      snackbar.open(displayError(e, "Unable to update remark."));
    }
  };

  const [deleteEventRemark] = useMutation(deleteEventRemarkMutation);
  const deleteRemark = () => {
    dialog.alert({
      title: "Delete remark?",
      message:
        "This action cannot be undone. Are you sure you want to delete this remark?",
      actions: [
        dialog.Actions.Cancel,
        {
          label: "Delete",
          onClick: async () => {
            try {
              dialog.close();
              await deleteEventRemark({ variables: { id: eventId, clientId } });
              snackbar.open("Remark successfully deleted.");
            } catch (e) {
              snackbar.open(displayError(e, "Unable to delete remark."));
            }
          },
        },
      ],
    });
  };

  return (
    <TimelineItem>
      <TimelineSeparator>
        <TimelineConnector
          className={classes.upConnector}
          sx={{
            visibility: isFirst ? "hidden" : "visible",
          }}
        />
        <TimelineDot className={classes.dot} />
        {!isLast && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent className={classes.content}>
        {editRemarkForm.shown && event ? (
          <RemarkForm
            defaultValues={{
              comment: event.message,
              createdAt: parseDateTime(event.createdAt),
            }}
            onCancel={editRemarkForm.hide}
            onSubmit={updateRemark}
          />
        ) : (
          <>
            {event?.type === "Event::Remark" && (
              <div className={classes.remarkHeader}>
                <Chip
                  className={classes.remarkChip}
                  label="Remarks"
                  size="small"
                />
                <IconButton
                  color="primary"
                  size="small"
                  onClick={optionMenu.show}
                >
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
                <Menu {...optionMenu.props}>
                  <MenuItem
                    onClick={() => {
                      optionMenu.hide();
                      editRemarkForm.show();
                    }}
                  >
                    <ListItemIcon>
                      <EditIcon fontSize="small" />
                    </ListItemIcon>
                    Edit
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      optionMenu.hide();
                      deleteRemark();
                    }}
                  >
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    Delete
                  </MenuItem>
                </Menu>
              </div>
            )}
            <Typography className={classes.message}>
              {event?.message}
            </Typography>
            <Typography className={classes.createdAt}>
              {format({ datetime: event?.createdAt }, "MMM d, yyyy, p")}
            </Typography>
          </>
        )}
      </TimelineContent>
    </TimelineItem>
  );
};

export default TimelineEntry;
